import { ApiError, errorMessage } from '@runnerboy/api-client';
import { CUSTOMER_PROGRESS_STEPS, progressIndex } from '@runnerboy/domain';
import type {
  CartDto,
  ComplianceBlockDto,
  OrderStatus,
  PricingBreakdownDto,
  ProductCardDto,
  StoreCardDto,
} from '@runnerboy/types';
import {
  Alert,
  Button,
  cx,
  inr,
  Packshot,
  ProductImage,
  QuantityStepper,
  Sheet,
  useToast,
  volume,
} from '@runnerboy/ui';
import { BadgeCheck, Bike, Check, Clock, Star } from 'lucide-react';
import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { client, useAsync, useSession } from '../lib/session';
import { StorefrontArt } from './art';

/** Soft stage colours behind packshots, by category family (never used as UI chrome). */
const STAGE: Record<string, [string, string]> = {
  beer: ['#FFF4D9', '#FFE1A1'],
  rtd: ['#FFE9DE', '#FFC8AE'],
  whisky: ['#F7EADB', '#E8C9A2'],
  brandy: ['#F6E6D8', '#E3BE9A'],
  rum: ['#F3E2D6', '#D9AE8C'],
  wine: ['#F8E6EC', '#E9B9C9'],
  liqueur: ['#EFE6F7', '#D2BFE9'],
  tequila: ['#F5F3D9', '#E2DB9C'],
  vodka: ['#EEF1F8', '#CCD5EA'],
  gin: ['#E5F4F1', '#B8E0D6'],
  water: ['#E8F1FD', '#BFD7F8'],
  soda: ['#E7F4F6', '#BFE1E6'],
  tonic: ['#EAF6EE', '#C2E6CF'],
  mixers: ['#FFF1E2', '#FFD3A6'],
  'soft-drinks': ['#FDEAE8', '#F6BDB6'],
  juices: ['#FFF3DD', '#FFD79A'],
  'energy-drinks': ['#E8EEFC', '#BFCDF6'],
  'non-alcoholic': ['#EAF6EE', '#C2E6CF'],
  snacks: ['#FCEEDB', '#F2CF9E'],
  namkeen: ['#FBEBD6', '#EDC690'],
  nuts: ['#F6E9DA', '#E3C49F'],
  ice: ['#E9F6FF', '#C3E5FB'],
};
export const stageStyle = (slug: string): CSSProperties => {
  const [a, b] = STAGE[slug] ?? ['#F3F4F7', '#DDE0E8'];
  return { background: `radial-gradient(120% 90% at 50% 18%, ${a} 0%, ${b} 100%)` };
};

// ---- Stores -------------------------------------------------------------------------------------------------------
export function StoreCard({
  store,
  selected,
  onSelect,
  wide,
}: {
  store: StoreCardDto;
  selected?: boolean;
  onSelect: () => void;
  wide?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onSelect}
      className={cx(
        'rb-press block shrink-0 overflow-hidden rounded-[24px] bg-white text-left shadow-[var(--shadow-card)]',
        wide ? 'w-full' : 'w-[282px]',
        selected && 'shadow-[0_0_0_2px_var(--color-marigold-500),var(--shadow-raised)]',
      )}
      aria-label={`${store.name}, ${store.etaMinutes} minutes`}
    >
      <div className="relative h-[104px]">
        <StorefrontArt name={store.name} className="absolute inset-0 h-full w-full" />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-night-900/85 px-2.5 py-1 text-[11px] font-semibold text-white shadow-[var(--shadow-raised)] ring-1 ring-white/10 backdrop-blur-md">
          <BadgeCheck className="h-3.5 w-3.5 text-marigold-500" aria-hidden />{' '}
          {store.verified
            ? t('home.licensed', 'Licensed · verified')
            : t('home.licensedStore', 'Licensed store')}
        </span>
        <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[12px] font-bold text-ink-900 shadow-[var(--shadow-raised)]">
          <Clock className="h-3.5 w-3.5 text-marigold-600" aria-hidden />
          <span className="rb-num">{t('common.minutes', { count: store.etaMinutes })}</span>
        </span>
      </div>
      <div className="p-4 pt-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-[16px] font-bold tracking-[-0.01em] text-ink-900">{store.name}</div>
            <div className="truncate text-[13px] text-ink-500">{store.locality}</div>
          </div>
          {store.rating !== null && (
            <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-success-50 px-2 py-0.5 text-[12px] font-bold text-success-600">
              <Star className="h-3 w-3 fill-current" aria-hidden />
              <span className="rb-num">{store.rating.toFixed(1)}</span>
            </span>
          )}
        </div>
        <div className="mt-3 flex items-center gap-2 text-[12px] font-medium text-ink-600">
          <span
            className={cx(
              'inline-flex items-center gap-1 font-semibold',
              store.isOpenNow ? 'text-success-600' : 'text-danger-600',
            )}
          >
            <span
              className={cx('h-1.5 w-1.5 rounded-full', store.isOpenNow ? 'bg-success-500' : 'bg-danger-500')}
              aria-hidden
            />
            {store.isOpenNow
              ? store.closesAt
                ? t('home.closesAt', { time: store.closesAt })
                : t('home.openNow')
              : t('home.closed')}
          </span>
          <span className="text-ink-300">·</span>
          <span className="rb-num">{t('common.km', { value: store.distanceKm.toFixed(1) })}</span>
          <span className="text-ink-300">·</span>
          <span>{t('home.fee', { fee: inr(store.deliveryFee) })}</span>
        </div>
        {!store.alcoholAvailable && store.unavailableReason && (
          <div className="mt-2 text-[12px] text-danger-600">{store.unavailableReason}</div>
        )}
      </div>
    </button>
  );
}

// ---- Cart actions ---------------------------------------------------------------------------------------------------
export function useCartActions() {
  const { cart, setCart, requireLogin, ageGate } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{
    product: ProductCardDto;
    qty: number;
    storeName: string;
  } | null>(null);
  const qtyOf = (storeProductId: string) =>
    cart?.items.find((i) => i.storeProductId === storeProductId)?.quantity ?? 0;
  const setQty = async (product: ProductCardDto, qty: number, replaceCart = false) => {
    if (!requireLogin()) return;
    if (product.isAgeRestricted && ageGate !== 'confirmed') return;
    setBusy(product.storeProductId);
    try {
      setCart(
        await client.put<CartDto>('/cart/items', {
          storeProductId: product.storeProductId,
          quantity: qty,
          replaceCart,
        }),
      );
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CART_STORE_MISMATCH')
        setConflict({
          product,
          qty,
          storeName: (e.details as { currentStoreName?: string })?.currentStoreName ?? '',
        });
      else toast('danger', errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  return { qtyOf, setQty, busy, conflict, setConflict };
}

// ---- Product card ------------------------------------------------------------------------------------------------------
export function ProductCard({
  product,
  actions,
  compact,
}: {
  product: ProductCardDto;
  actions: ReturnType<typeof useCartActions>;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const qty = actions.qtyOf(product.storeProductId);
  const off =
    product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  const low = product.inStock && product.available <= 3;
  const [open, setOpen] = useState(false);
  return (
    <article className="flex h-full flex-col overflow-hidden rounded-[22px] bg-white shadow-[var(--shadow-card)]">
      <div className="relative aspect-square" style={stageStyle(product.categorySlug)}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="absolute inset-0 z-0"
          aria-label={t('catalog.details', { name: product.name, defaultValue: `Details: ${product.name}` })}
        />
        <ProductImage
          url={product.imageUrl}
          categorySlug={product.categorySlug}
          alt={product.name}
          brand={product.brand}
          volumeMl={product.volumeMl}
          className={cx(
            'pointer-events-none absolute inset-[9%] h-[82%] w-[82%] drop-shadow-[0_10px_14px_rgb(14_19_36/0.22)]',
            !product.inStock && 'opacity-45 grayscale',
          )}
        />
        {product.isAgeRestricted && (
          <span className="rb-num absolute left-2.5 top-2.5 rounded-full bg-ink-900/90 px-2 py-0.5 text-[10.5px] font-semibold text-white">
            21+
          </span>
        )}
        {off > 0 && (
          <span className="absolute right-2.5 top-2.5 rounded-full bg-success-500 px-2 py-0.5 text-[10.5px] font-bold text-white">
            {off}% off
          </span>
        )}
        {!product.inStock && (
          <span className="absolute inset-x-0 bottom-3 mx-auto w-fit rounded-full bg-white/90 px-3 py-1 text-[12px] font-bold text-ink-700 shadow-[var(--shadow-card)] backdrop-blur">
            {t('catalog.outOfStock')}
          </span>
        )}
        {product.inStock && (
          <div className="absolute bottom-2.5 right-2.5 z-10">
            <QuantityStepper
              value={qty}
              onChange={(v) => void actions.setQty(product, v)}
              max={Math.min(product.available, 24)}
              label={product.name}
              busy={actions.busy === product.storeProductId}
              size={compact ? 'sm' : 'md'}
            />
          </div>
        )}
      </div>
      <div className={cx('flex flex-1 flex-col', compact ? 'p-2.5' : 'p-3')}>
        <div className="rb-eyebrow truncate text-[9.5px] text-ink-400">{product.brand}</div>
        <h3 className="mt-0.5 line-clamp-2 min-h-[2.5rem] text-[14px] font-semibold leading-[1.25] tracking-[-0.01em] text-ink-900">
          <button type="button" onClick={() => setOpen(true)} className="text-left">
            {product.name}
          </button>
        </h3>
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {product.volumeMl && (
            <span className="rb-num rounded-full bg-ink-900/[0.05] px-2 py-0.5 text-[11px] font-medium text-ink-600">
              {volume(product.volumeMl)}
            </span>
          )}
          {product.abv ? (
            <span className="rb-num rounded-full bg-ink-900/[0.05] px-2 py-0.5 text-[11px] font-medium text-ink-600">
              {product.abv}%
            </span>
          ) : null}
          {low && (
            <span className="rounded-full bg-marigold-100 px-2 py-0.5 text-[11px] font-semibold text-marigold-700">
              {t('catalog.onlyLeft', { count: product.available })}
            </span>
          )}
        </div>
        <div className="mt-auto flex items-baseline gap-1.5 pt-2.5">
          <span className="rb-display text-[19px] font-bold leading-none text-ink-900">
            {inr(product.price)}
          </span>
          {off > 0 && (
            <span className="rb-num text-[12px] text-ink-400 line-through">{inr(product.mrp)}</span>
          )}
        </div>
      </div>
      <ProductDetailSheet product={product} actions={actions} open={open} onClose={() => setOpen(false)} />
    </article>
  );
}

/** Pairings shown on the details sheet: accompaniments the same store usually carries. */
const PAIRS: Record<string, string[]> = {
  beer: ['snacks', 'namkeen', 'ice'],
  whisky: ['soda', 'ice', 'water'],
  rum: ['soft-drinks', 'ice', 'soda'],
  vodka: ['mixers', 'tonic', 'ice'],
  gin: ['tonic', 'ice'],
  wine: ['snacks', 'water'],
  tequila: ['mixers', 'ice'],
  brandy: ['soda', 'water'],
};

export function ProductDetailSheet({
  product: initial,
  actions,
  open,
  onClose,
}: {
  product: ProductCardDto;
  actions: ReturnType<typeof useCartActions>;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { storeId } = useSession();
  const [chosen, setChosen] = useState<string | null>(null);
  // Other bottle sizes of the same product in this store: fetched only while the sheet is open.
  const sizes = useAsync(
    (sig) =>
      client
        .get<{
          items: ProductCardDto[];
        }>(`/public/stores/${storeId}/catalog?q=${encodeURIComponent(initial.name)}&limit=12`, sig)
        .then((r) =>
          r.items
            .filter((p) => p.productId === initial.productId)
            .sort((a, b) => (a.volumeMl ?? 0) - (b.volumeMl ?? 0)),
        ),
    [open, storeId, initial.productId],
    { enabled: open && !!storeId },
  );
  const variants = sizes.data && sizes.data.length > 1 ? sizes.data : null;
  const product = variants?.find((v) => v.storeProductId === chosen) ?? initial;
  const qty = actions.qtyOf(product.storeProductId);
  const off =
    product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  const pairs = PAIRS[product.categorySlug] ?? [];
  const facts: [string, string][] = [
    ...(product.volumeMl
      ? ([[t('catalog.volumeLabel', 'Volume'), volume(product.volumeMl)]] as [string, string][])
      : []),
    ...(product.abv
      ? ([[t('catalog.abvLabel', 'Alcohol'), `${product.abv}% ABV`]] as [string, string][])
      : []),
    ...(product.packSize > 1
      ? ([[t('catalog.pack', 'Pack'), `${product.packSize} units`]] as [string, string][])
      : []),
    [t('catalog.category', 'Aisle'), product.categoryName],
  ];
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={product.brand}
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="rb-num text-[13px] text-ink-500">
            {qty > 0 ? t('common.items', { count: qty }) : product.categoryName}
          </div>
          {product.inStock ? (
            <QuantityStepper
              value={qty}
              onChange={(v) => void actions.setQty(product, v)}
              max={Math.min(product.available, 24)}
              label={product.name}
              busy={actions.busy === product.storeProductId}
              size="lg"
            />
          ) : (
            <Button variant="secondary" disabled>
              {t('catalog.outOfStock')}
            </Button>
          )}
        </div>
      }
    >
      <div
        className="-mx-5 -mt-1 grid aspect-[16/11] place-items-center"
        style={stageStyle(product.categorySlug)}
      >
        <ProductImage
          url={product.imageUrl}
          categorySlug={product.categorySlug}
          alt={product.name}
          brand={product.brand}
          volumeMl={product.volumeMl}
          className={cx(
            'h-[78%] w-[60%] drop-shadow-[0_18px_22px_rgb(14_19_36/0.25)]',
            !product.inStock && 'opacity-45 grayscale',
          )}
        />
      </div>
      <div className="mt-4 flex items-start justify-between gap-3">
        <h2 className="rb-display text-[24px] font-bold leading-tight text-ink-900">{product.name}</h2>
        {product.isAgeRestricted && (
          <span className="rb-num mt-1 shrink-0 rounded-full bg-ink-900 px-2.5 py-1 text-[11px] font-semibold text-white">
            21+
          </span>
        )}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="rb-display text-[28px] font-bold text-ink-900">{inr(product.price)}</span>
        {off > 0 && (
          <>
            <span className="rb-num text-[14px] text-ink-400 line-through">{inr(product.mrp)}</span>
            <span className="rounded-full bg-success-50 px-2 py-0.5 text-[12px] font-bold text-success-600">
              {off}% off
            </span>
          </>
        )}
      </div>
      <p className="mt-1 text-[12px] text-ink-500">{t('catalog.inclTaxes')}</p>
      {variants && (
        <div className="mt-4">
          <div className="rb-eyebrow text-ink-500">{t('catalog.sizes', 'Size')}</div>
          <div
            className="mt-2 flex flex-wrap gap-2"
            role="radiogroup"
            aria-label={t('catalog.sizes', 'Size')}
          >
            {variants.map((v) => (
              <button
                key={v.storeProductId}
                type="button"
                role="radio"
                aria-checked={v.storeProductId === product.storeProductId}
                disabled={!v.inStock}
                onClick={() => setChosen(v.storeProductId)}
                className={cx(
                  'rb-press flex min-w-[84px] flex-col items-start rounded-[16px] px-3.5 py-2 text-left disabled:opacity-40',
                  v.storeProductId === product.storeProductId
                    ? 'bg-ink-900 text-white'
                    : 'bg-white text-ink-800 ring-1 ring-ink-900/[0.1]',
                )}
              >
                <span className="rb-num text-[13px] font-semibold">
                  {v.volumeMl ? volume(v.volumeMl) : v.packSize > 1 ? `${v.packSize} pack` : '—'}
                </span>
                <span
                  className={cx(
                    'rb-num text-[12px]',
                    v.storeProductId === product.storeProductId ? 'text-white/70' : 'text-ink-500',
                  )}
                >
                  {inr(v.price)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
      <dl className="mt-4 grid grid-cols-2 gap-2">
        {facts.map(([k, v]) => (
          <div key={k} className="rounded-[16px] bg-ink-900/[0.04] px-3 py-2.5">
            <dt className="rb-eyebrow text-[9.5px] text-ink-500">{k}</dt>
            <dd className="rb-num mt-0.5 text-[14px] font-semibold text-ink-900">{v}</dd>
          </div>
        ))}
      </dl>
      <div
        className={cx(
          'mt-3 flex items-center gap-2 text-[13px] font-semibold',
          product.inStock
            ? product.available <= 3
              ? 'text-marigold-700'
              : 'text-success-600'
            : 'text-danger-600',
        )}
      >
        <span
          className={cx(
            'h-2 w-2 rounded-full',
            product.inStock
              ? product.available <= 3
                ? 'bg-marigold-500'
                : 'bg-success-500'
              : 'bg-danger-500',
          )}
          aria-hidden
        />
        {product.inStock
          ? product.available <= 3
            ? t('catalog.onlyLeft', { count: product.available })
            : t('catalog.inStock', 'In stock at this store')
          : t('catalog.outOfStock')}
      </div>
      {product.isAgeRestricted && (
        <p className="mt-3 rounded-[16px] bg-peacock-50 p-3 text-[12.5px] leading-relaxed text-peacock-800">
          {t(
            'catalog.ageNote',
            'Sold by the licensed store to adults of legal drinking age. Your runner checks an original ID at the door.',
          )}
        </p>
      )}
      {pairs.length > 0 && storeId && (
        <div className="mt-4">
          <div className="rb-eyebrow text-ink-500">{t('catalog.pairsWith', 'Goes well with')}</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {pairs.map((slug) => (
              <Link
                key={slug}
                to={`/store/${storeId}?category=${slug}`}
                onClick={onClose}
                className="rb-press inline-flex h-9 items-center gap-1.5 rounded-full bg-white pl-1 pr-3.5 text-[13px] font-semibold text-ink-800 shadow-[var(--shadow-card)]"
              >
                <span className="grid h-7 w-7 place-items-center rounded-full" style={stageStyle(slug)}>
                  <Packshot brand="" name={slug} categorySlug={slug} volumeMl={null} className="h-6 w-6" />
                </span>
                <span className="capitalize">{slug.replace('-', ' ')}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </Sheet>
  );
}

export function CartConflictSheet({ actions }: { actions: ReturnType<typeof useCartActions> }) {
  const { t } = useTranslation();
  const c = actions.conflict;
  return (
    <Sheet open={!!c} onClose={() => actions.setConflict(null)} title={t('cart.replaceTitle')}>
      <p className="text-sm text-ink-600">{t('cart.replaceBody', { store: c?.storeName })}</p>
      <div className="mt-5 flex flex-col gap-2">
        <Button
          block
          size="lg"
          onClick={() => {
            if (c) void actions.setQty(c.product, c.qty, true);
            actions.setConflict(null);
          }}
        >
          {t('cart.replaceConfirm')}
        </Button>
        <Button variant="ghost" block onClick={() => actions.setConflict(null)}>
          {t('common.cancel')}
        </Button>
      </div>
    </Sheet>
  );
}

// ---- Bill ------------------------------------------------------------------------------------------------------------------
export function BillDetails({
  pricing,
  storeName,
}: {
  pricing: PricingBreakdownDto;
  storeName?: string | null;
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded-[22px] bg-white p-4 shadow-[var(--shadow-card)]">
      <h3 className="rb-eyebrow text-ink-500">{t('cart.billDetails')}</h3>
      <dl className="mt-3 flex flex-col gap-2.5 text-[14px]">
        {pricing.lines
          .filter((l) => l.kind !== 'TOTAL')
          .map((l) => (
            <div key={l.key} className="flex items-start justify-between gap-3">
              <dt className="text-ink-600">
                {l.label}
                {l.note && (
                  <span className="block text-[12px] text-ink-400">
                    {l.key === 'items' && storeName
                      ? t('common.sellerOfRecord', { store: storeName })
                      : l.note}
                  </span>
                )}
              </dt>
              <dd className={cx('rb-num font-medium', l.amount < 0 ? 'text-success-600' : 'text-ink-900')}>
                {inr(l.amount, { decimals: true })}
              </dd>
            </div>
          ))}
        <div className="mt-1 flex items-center justify-between border-t border-dashed border-ink-200 pt-3.5">
          <dt className="text-[15px] font-bold text-ink-900">{t('common.total')}</dt>
          <dd className="rb-display text-[22px] font-bold text-ink-900">
            {inr(pricing.total, { decimals: true })}
          </dd>
        </div>
      </dl>
    </section>
  );
}

export function ComplianceBlocks({ blocks }: { blocks: ComplianceBlockDto[] }) {
  const { t } = useTranslation();
  if (blocks.length === 0) return null;
  const unique = blocks.filter((b, i) => blocks.findIndex((x) => x.code === b.code) === i);
  return (
    <Alert tone="danger" title={t('checkout.blocked')}>
      <ul className="mt-1 list-disc pl-4">
        {unique.map((b) => (
          <li key={b.code}>{t(`compliance.${b.code}`, { defaultValue: b.message })}</li>
        ))}
      </ul>
    </Alert>
  );
}

// ---- Progress ----------------------------------------------------------------------------------------------------------------
export function ProgressTimeline({
  status,
  history,
}: {
  status: OrderStatus;
  history: { status: OrderStatus; at: string }[];
}) {
  const { t } = useTranslation();
  const idx = progressIndex(status);
  const atOf = (statuses: OrderStatus[]) => history.find((h) => statuses.includes(h.status))?.at;
  return (
    <ol className="flex flex-col">
      {CUSTOMER_PROGRESS_STEPS.map((step, i) => {
        const done = idx > i || (idx === i && status === 'DELIVERED');
        const current = idx === i && status !== 'DELIVERED';
        const at = atOf(step.statuses);
        return (
          <li key={step.key} className="relative flex gap-3 pb-4 last:pb-0">
            {i < CUSTOMER_PROGRESS_STEPS.length - 1 && (
              <span
                className={cx(
                  'absolute left-[13px] top-7 h-[calc(100%-16px)] w-[2px] rounded-full',
                  done ? 'bg-ink-900' : 'bg-ink-200',
                )}
                aria-hidden
              />
            )}
            <span
              className={cx(
                'relative z-10 grid h-7 w-7 shrink-0 place-items-center rounded-full',
                done
                  ? 'bg-ink-900 text-marigold-500'
                  : current
                    ? 'bg-marigold-500 text-ink-950 shadow-[var(--shadow-glow)]'
                    : 'bg-ink-100 text-ink-400',
              )}
            >
              {done ? (
                <Check className="h-4 w-4" strokeWidth={3} />
              ) : current ? (
                <Bike className="h-4 w-4" />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
              )}
            </span>
            <div className="min-w-0 pt-1">
              <div
                className={cx('text-[14px] font-semibold', done || current ? 'text-ink-900' : 'text-ink-400')}
              >
                {t(`progress.${step.key}`)}
              </div>
              {at && (done || current) && (
                <div className="rb-num text-[12px] text-ink-500">
                  {new Date(at).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ---- Categories --------------------------------------------------------------------------------------------------------------
export function CategoryTile({ slug, name, to }: { slug: string; name: string; to: string }) {
  return (
    <Link to={to} className="rb-press flex flex-col items-center gap-2 text-center">
      <span
        className="relative grid aspect-square w-full place-items-center overflow-hidden rounded-[22px] shadow-[var(--shadow-card)]"
        style={stageStyle(slug)}
      >
        <Packshot
          brand=""
          name={name}
          categorySlug={slug}
          volumeMl={slug === 'beer' ? 650 : null}
          className="h-[78%] w-[78%] drop-shadow-[0_8px_10px_rgb(14_19_36/0.2)]"
        />
      </span>
      <span className="line-clamp-2 text-[12px] font-semibold leading-tight text-ink-800">{name}</span>
    </Link>
  );
}
